import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  signal,
  SimpleChanges,
  viewChild,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MarkdownModule } from 'ngx-markdown';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { debounceTime, Subject, Subscription } from 'rxjs';
import { AuthService } from 'src/app/auth.service';
import { SelectComponent, SelectOption } from 'src/app/shared/components/ui/select/select.component';
import { ZoomControlComponent } from 'src/app/shared/components/ui/zoom-control/zoom-control.component';
import { ZoomableDirective } from 'src/app/shared/directives/zoomable.directive';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { ChunkService } from '../../chunk/chunk.service';
import { ContentStateService } from '../../content-state.service';
import { MONACO_THEMES } from './monaco-themes';

@Component({
  selector: 'app-code-editor-canvas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatIconModule,
    SelectComponent,
    MatTooltipModule,
    TranslateModule,
    MonacoEditorModule,
    MarkdownModule,
    ZoomControlComponent,
    ZoomableDirective,
  ],
  template: `
    <div class="code-editor-canvas">
      <div class="code-editor-header">
        <div class="header-left">
          <button mat-icon-button class="toggle-sidebar-btn" (click)="sidenavService.toggle()">
            <mat-icon>{{ sidenavService.isSmallOrTablet() ? 'menu' : 'left_panel_open' }}</mat-icon>
          </button>
          <mat-icon class="header-icon">code</mat-icon>
          <input class="title-input" [(ngModel)]="editableTitle" (blur)="onTitleBlur()" (keydown.enter)="$event.target.blur()" placeholder="Title" [disabled]="!isWriter()" />
        </div>

        <div class="header-center">
          <ui-select [options]="codeLanguages" [(ngModel)]="selectedLanguage" (valueChange)="onLanguageChange($any($event))" [compact]="true" [disabled]="!isWriter()" />
          <ui-select [options]="themes" [(ngModel)]="selectedTheme" (valueChange)="onThemeChange($any($event))" [compact]="true" />
        </div>

        <div class="actions">
          @if (saveStatus() === 'saved') {
            <mat-icon class="success-icon">check_circle</mat-icon>
            <span class="status-text">{{ 'Global.Saved' | translate }}</span>
          }

          @if (selectedLanguage === 'mermaid' || selectedLanguage === 'markdown') {
            <button mat-icon-button (click)="togglePreview()" [matTooltip]="(showPreview() ? 'Global.HidePreview' : 'Global.ShowPreview') | translate">
              <mat-icon>{{ showPreview() ? 'visibility_off' : 'visibility' }}</mat-icon>
            </button>
          }

          <button mat-icon-button (click)="saveAndClose()" [matTooltip]="'Global.Close' | translate">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <div class="code-editor-content" [class.split-layout]="showPreview() && (selectedLanguage === 'mermaid' || selectedLanguage === 'markdown')">
        <div class="editor-pane">
          <ngx-monaco-editor [options]="editorOptions" (onInit)="onEditorInit($event)" />
        </div>
        @if (showPreview()) {
          @if (selectedLanguage === 'mermaid') {
            <div #previewPane #zoom="appZoomable" appZoomable [minScale]="0.5" [maxScale]="3.0" class="preview-pane canvas-mode" [class.grabbing]="zoom.isDragging()">
              <app-zoom-control class="zoom-toolbar" [(zoomScale)]="zoom.zoomScale" [minScale]="0.5" [maxScale]="3.0" (reset)="zoom.resetZoom()" />
              <div
                #mermaidPreview
                class="mermaid"
                [style.transform]="'translate(' + zoom.panX() + 'px, ' + zoom.panY() + 'px) scale(' + zoom.zoomScale() + ')'"
                [style.transition]="zoom.isDragging() ? 'none' : 'transform 0.2s ease-out'"
                style="transform-origin: center center;"
              ></div>
            </div>
          } @else if (selectedLanguage === 'markdown') {
            <div class="markdown-preview-pane custom-scrollbar">
              <markdown [data]="markdownContent()"></markdown>
            </div>
          }
        }
      </div>
    </div>
  `,
  styleUrls: ['./code-editor-canvas.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CodeEditorCanvasComponent implements OnChanges, OnDestroy {
  @Input() contentId: string;
  @Input() chunkId: string;
  @Input() chunkTitle: string = '';
  @Input() content: string = '';
  @Input() codeLanguage: string = 'javascript';
  @Input() edition: any;
  @Output() close = new EventEmitter<void>();

  @ViewChild('mermaidPreview') mermaidPreviewEl!: ElementRef<HTMLDivElement>;
  @ViewChild('previewPane') previewPaneEl!: ElementRef<HTMLDivElement>;

  public contentStateService = inject(ContentStateService);
  public sidenavService = inject(SidenavService);
  private chunkService = inject(ChunkService);
  private translate = inject(TranslateService);
  private authService = inject(AuthService);

  isWriter = computed(() => this.authService.isWriter() && !this.contentStateService.isReader());
  selectedLanguage = 'javascript';
  editableTitle = '';
  saveStatus = signal<'' | 'saved'>('');
  displayTitle = signal('');
  selectedTheme = 'vs-dark-plus';
  showPreview = signal(false);
  zoomable = viewChild(ZoomableDirective);
  markdownContent = signal('');

  themes: SelectOption[] = [
    { value: 'vs-light-plus', label: 'Global.ThemeLight' },
    { value: 'vs-dark-plus', label: 'Global.ThemeDark' },
    { value: 'monokai', label: 'Monokai' },
    { value: 'solarized-light', label: 'Global.ThemeSolarizedLight' },
    { value: 'solarized-dark', label: 'Global.ThemeSolarizedDark' },
  ];

  private editor: any = null;
  private localContent = '';
  private contentChangeDisposable: any = null;
  private saveSubject = new Subject<void>();
  private saveSubscription: Subscription;
  private editorInitialized = false;

  codeLanguages: SelectOption[] = [
    { value: 'javascript', label: 'JavaScript' },
    { value: 'typescript', label: 'TypeScript' },
    { value: 'python', label: 'Python' },
    { value: 'clike', label: 'C++' },
    { value: 'rust', label: 'Rust' },
    { value: 'css', label: 'CSS' },
    { value: 'java', label: 'Java' },
    { value: 'csharp', label: 'C#' },
    { value: 'php', label: 'PHP' },
    { value: 'go', label: 'Go' },
    { value: 'ruby', label: 'Ruby' },
    { value: 'html', label: 'HTML' },
    { value: 'vb', label: 'Visual Basic' },
    { value: 'sql', label: 'SQL' },
    { value: 'bash', label: 'Bash' },
    { value: 'mermaid', label: 'Mermaid' },
    { value: 'markdown', label: 'Markdown' },
  ];

  private monacoLangMap: Record<string, string> = {
    javascript: 'javascript',
    typescript: 'typescript',
    python: 'python',
    clike: 'cpp',
    rust: 'rust',
    css: 'css',
    java: 'java',
    csharp: 'csharp',
    php: 'php',
    go: 'go',
    ruby: 'ruby',
    html: 'html',
    vb: 'vb',
    sql: 'sql',
    bash: 'shell',
    mermaid: 'plaintext',
    markdown: 'markdown',
  };

  editorOptions = {
    theme: 'vs-dark-plus',
    language: 'javascript',
    automaticLayout: true,
    scrollBeyondLastLine: false,
    minimap: { enabled: false },
    fontSize: 14,
    fontFamily: "'JetBrains Mono', 'Fira Code', 'Fira Mono', 'SF Mono', Consolas, Monaco, monospace",
    fontLigatures: true,
    tabSize: 4,
    insertSpaces: true,
    detectIndentation: false,
    lineNumbers: 'on' as const,
    roundedSelection: true,
    readOnly: false,
    wordWrap: 'on' as const,
    padding: { top: 16 },
    'semanticHighlighting.enabled': false,
  };

  constructor() {
    this.editorOptions.readOnly = !this.isWriter();

    this.saveSubscription = this.saveSubject.pipe(debounceTime(2000)).subscribe(() => {
      this.performSave();
    });

    const savedTheme = localStorage.getItem('monaco-editor-theme');
    if (savedTheme && this.themes.some((t) => t.value === savedTheme)) {
      this.selectedTheme = savedTheme;
      this.editorOptions.theme = savedTheme;
    } else {
      this.selectedTheme = 'vs-dark-plus';
      this.editorOptions.theme = 'vs-dark-plus';
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['content'] && !this.editorInitialized) {
      this.localContent = this.content || '';
      this.markdownContent.set(this.localContent);
    }
    if (changes['chunkTitle']) {
      this.editableTitle = this.chunkTitle || '';
      this.displayTitle.set(this.chunkTitle || '');
    }
    if (changes['codeLanguage']) {
      this.selectedLanguage = this.codeLanguage || 'javascript';
      this.updateLanguage();
    }
  }

  ngOnDestroy() {
    this.saveSubscription.unsubscribe();
    if (this.contentChangeDisposable) {
      this.contentChangeDisposable.dispose();
    }
    if (this.editor) {
      const model = this.editor.getModel();
      if (model) {
        model.dispose();
      }
      this.editor = null;
    }
  }

  onEditorInit(editor: any) {
    this.editor = editor;
    this.editorInitialized = true;

    this.editor.setValue(this.localContent || '');

    this.contentChangeDisposable = this.editor.onDidChangeModelContent(() => {
      this.localContent = this.editor.getValue();
      if (this.isWriter()) {
        this.saveSubject.next();
      }
      if (this.showPreview()) {
        if (this.selectedLanguage === 'mermaid') {
          this.renderMermaidPreview();
        } else if (this.selectedLanguage === 'markdown') {
          this.markdownContent.set(this.localContent);
        }
      }
    });

    const monacoInstance = (window as any).monaco;
    if (monacoInstance) {
      // Define custom themes
      Object.entries(MONACO_THEMES).forEach(([name, definition]) => {
        monacoInstance.editor.defineTheme(name, definition);
      });

      monacoInstance.editor.setModelMarkers(editor.getModel(), 'code-editor', []);

      monacoInstance.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: true,
      });
      monacoInstance.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        noSemanticValidation: true,
        noSyntaxValidation: true,
      });

      monacoInstance.editor.setTheme(this.selectedTheme);
    }

    this.updateLanguage();
    if (this.showPreview()) {
      if (this.selectedLanguage === 'mermaid') {
        setTimeout(() => this.renderMermaidPreview(), 50);
      } else if (this.selectedLanguage === 'markdown') {
        this.markdownContent.set(this.localContent);
      }
    }
  }

  onThemeChange(theme: string) {
    this.selectedTheme = theme;
    localStorage.setItem('monaco-editor-theme', theme);
    this.editorOptions = {
      ...this.editorOptions,
      theme: theme,
    };
    if (this.editor && (window as any).monaco) {
      (window as any).monaco.editor.setTheme(theme);
    }
  }

  onLanguageChange(lang: string) {
    this.codeLanguage = lang;
    this.selectedLanguage = lang;
    this.updateLanguage();
    this.performSave();
    if (lang !== 'mermaid' && lang !== 'markdown') {
      this.showPreview.set(false);
    } else if (this.showPreview()) {
      if (lang === 'mermaid') {
        setTimeout(() => this.renderMermaidPreview(), 50);
      } else if (lang === 'markdown') {
        this.markdownContent.set(this.localContent);
      }
    }
  }

  onTitleBlur() {
    this.displayTitle.set(this.editableTitle);
  }

  togglePreview() {
    this.showPreview.update((v) => !v);
    this.zoomable()?.resetZoom();
    if (this.showPreview()) {
      if (this.selectedLanguage === 'mermaid') {
        setTimeout(() => this.renderMermaidPreview(), 50);
      } else if (this.selectedLanguage === 'markdown') {
        this.markdownContent.set(this.localContent);
      }
    }
  }

  private async renderMermaidPreview() {
    if (!this.mermaidPreviewEl) return;
    const content = this.localContent || '';
    if (!content.trim()) {
      this.mermaidPreviewEl.nativeElement.innerHTML = '<div class="preview-empty">No diagram content</div>';
      return;
    }

    const elementId = `editor-mermaid-${this.chunkId || 'new'}`.replace(/[^a-zA-Z0-9-]/g, '-');
    try {
      const mermaid = (await import('mermaid')).default;
      mermaid.initialize({
        startOnLoad: false,
        theme: 'default',
        securityLevel: 'loose',
      });

      const { svg } = await mermaid.render(elementId, content);

      if (this.mermaidPreviewEl) {
        this.mermaidPreviewEl.nativeElement.innerHTML = svg;
      }
    } catch (err) {
      // Ignore intermediate syntax errors during typing
      console.warn('Mermaid rendering syntax error (expected during typing):', err);
      const badEl = document.getElementById(elementId) || document.getElementById(`d${elementId}`);
      if (badEl) {
        badEl.remove();
      }
    }
  }

  private updateLanguage() {
    const monacoLang = this.monacoLangMap[this.codeLanguage] || this.codeLanguage || 'javascript';
    if (this.editor) {
      const model = this.editor.getModel();
      if (model) {
        (window as any).monaco.editor.setModelLanguage(model, monacoLang);
      }
    }
  }

  saveAndClose() {
    if (!this.isWriter() || !this.chunkId || !this.edition) {
      this.close.emit();
      return;
    }

    const content = this.getCurrentContent();
    const edition = { ...this.edition, codeLanguage: this.codeLanguage };
    const editData = {
      id: this.chunkId,
      edition,
      content,
      contentID: this.contentId,
      language: this.translate.currentLang,
      title: this.editableTitle,
    };

    this.chunkService.editChunk(editData).subscribe({
      next: () => {
        this.contentStateService.getPieces(true, true);
      },
      error: (err) => {
        console.error('Save failed', err);
      },
    });

    this.close.emit();
  }

  private getCurrentContent(): string {
    return this.editor ? this.editor.getValue() : this.localContent;
  }

  performSave() {
    if (!this.isWriter() || !this.chunkId || !this.edition) return;
    const content = this.getCurrentContent();
    const edition = { ...this.edition, codeLanguage: this.codeLanguage };
    const editData = {
      id: this.chunkId,
      edition,
      content,
      contentID: this.contentId,
      language: this.translate.currentLang,
      title: this.editableTitle,
    };

    this.chunkService.editChunk(editData).subscribe({
      next: () => {
        this.contentStateService.getPieces(true, true);
        this.saveStatus.set('saved');
        setTimeout(() => {
          if (this.saveStatus() === 'saved') {
            this.saveStatus.set('');
          }
        }, 2000);
      },
      error: (err) => console.error('Autosave failed', err),
    });
  }
}
